import ProxyImage from '@shared/ProxyImage/ProxyImage';
import classNames from '@sindresorhus/class-names';
import Image from 'next/image';
import Link from 'next/link';
import {useCallback, useState} from 'react';
import type {Swiper as ISwiper} from 'swiper';
import {Navigation} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import chevron from '@web-landing/assets/images/chevron_white.svg';

import {miniPicksTestIds} from '@web-landing/constants/test-ids/mainLandingTestIds';

import {TVBlock as TVBlockType} from '@web-landing/strapi/types';

import {logTmrEvent} from '@web-landing/analytics/helpers/logTmrEvent';

import {EVENTS} from '@web-landing/analytics/events';

import oldStyles from '../TVBlock.module.scss';
import newStyles from '../TVBlock_redesign.module.scss';

type Props = {
  redesign?: boolean;
  compilationTitle?: string;
} & Pick<TVBlockType, 'tvApps' | 'catalogLinkText'>;

const TVBlockSwiper = ({
  tvApps,
  redesign,
  catalogLinkText = '',
  compilationTitle,
}: Props) => {
  const styles = redesign ? newStyles : oldStyles;
  const [swiperInstance, setSwiperInstance] = useState<ISwiper>();

  const [swiperState, setSwiperState] = useState<{
    isBeginning: boolean;
    isEnd: boolean;
  }>();

  const onClick = useCallback((href: string) => {
    logTmrEvent(EVENTS.appSelect, {
      compilation_id: 'landing_app_compilation_OUtDw5',
      compilation_name: 'RuStore для ТВ',
      compilation_type: 'grid',
      app_packageName: href.split('/').pop(),
    });
  }, []);
  const onGoAllClick = () => {
    logTmrEvent(EVENTS.landingClick, {
      button_text: catalogLinkText,
      compilation_name: compilationTitle,
    });
  };

  return (
    <div className={styles.swiper}>
      <button
        onClick={() => swiperInstance?.slidePrev()}
        className={classNames(styles.arrow, styles.arrow__left, {
          [styles.arrow__disabled]: swiperState?.isBeginning,
        })}
        data-testid={miniPicksTestIds.miniPicksBackButton}
        aria-label="Назад"
      >
        <Image
          src={chevron.src}
          alt=""
          aria-hidden={true}
          width={32}
          height={32}
        />
      </button>
      <Swiper
        spaceBetween={0}
        onSwiper={(swiper) => setSwiperInstance(swiper)}
        modules={[Navigation]}
        onProgress={(e) =>
          setSwiperState({isBeginning: e.isBeginning, isEnd: e.isEnd})
        }
        slidesPerView="auto"
      >
        {tvApps.map((app, index) => (
          <SwiperSlide
            key={index}
            className={classNames(styles.link, {
              [styles.disableLink]: !app.href,
            })}
          >
            {app.href && (
              <Link
                href={app.href}
                target={app.isTargetBlank ? '_blank' : '_self'}
                onClick={() => onClick(`${app.href}`)}
              >
                <ProxyImage
                  imageData={app.backgroundImage}
                  className={styles.linkImage}
                />
              </Link>
            )}
            {!app.href && (
              <ProxyImage
                imageData={app.backgroundImage}
                className={styles.linkImage}
              />
            )}
          </SwiperSlide>
        ))}
        {redesign && catalogLinkText && (
          <SwiperSlide className={styles.allAppsLink}>
            <Link
              href="/catalog/tv"
              target="_blank"
              onClick={onGoAllClick}
              prefetch={false}
            >
              {catalogLinkText}
            </Link>
          </SwiperSlide>
        )}
      </Swiper>
      <button
        data-testid={miniPicksTestIds.miniPicksNextButton}
        className={classNames(styles.arrow, {
          [styles.arrow__disabled]: swiperState?.isEnd,
        })}
        onClick={() => swiperInstance?.slideNext()}
        aria-label="Вперед"
      >
        <Image
          src={chevron.src}
          alt=""
          aria-hidden={true}
          width={32}
          height={32}
        />
      </button>
    </div>
  );
};
export default TVBlockSwiper;
